/**
 * Config-driven PostHog event filter for `before_send`.
 *
 * Reduces event volume by ~90% while preserving 100% of custom event data
 * on key pages (e.g. /cupons/).
 *
 * Evaluation order per event:
 *   1. Not in config? Check '*' (custom events only). No match → SEND (100%)
 *   2. enabled: false → DROP
 *   3. URL matches an exclude pattern → DROP
 *   4. weekdays set, today not in list → DROP
 *   5. paths set, URL matches a rule → use that rule's sample rate
 *   6. No path match / no paths → use fallback sample rate
 *   7. No sample defined → SEND (100%)
 */

/**
 * @typedef {Object} PathRule
 * @property {string} match - Substring to match against pathname
 * @property {number} sample - Sample rate for this path (0–1)
 */

/**
 * @typedef {Object} EventFilterRule
 * @property {boolean} enabled - Whether this event type is enabled at all
 * @property {number} [sample] - Fallback sample rate (0–1); omit to send 100%
 * @property {string[]} [exclude] - Pathnames that always drop the event
 * @property {number[]} [weekdays] - Days to capture (0=Sun..6=Sat); omit=all
 * @property {PathRule[]} [paths] - Path-specific sample rates; first match wins
 */

/**
 * Default filter configuration.
 *
 * PostHog internal events ($) are listed explicitly.
 * Custom events (no $ prefix) fall through to the '*' wildcard entry.
 *
 * Current sampling summary:
 *
 *   Event                  | /cupons/ | /promocoes/ | Elsewhere
 *   -----------------------|----------|-------------|----------
 *   $identify              |   50%    |    drop     |   drop
 *   $feature_flag_called   |   off    |    off      |   off
 *   $rageclick             |   off    |    off      |   off
 *   $$heatmap              |   off    |    off      |   off
 *   $web_vitals            |   50%    |    drop     |   drop
 *   $pageleave             |   off    |    off      |   off
 *   $pageview              |  100%    |    50%      |   10%
 *   $set                   |   10%    |    10%      |   10%
 *   $autocapture           |  100%    |    drop     |   drop
 *   Custom events (*)      |   50%    |    50%      |   10%
 *
 * To change: edit the rules below and update this table.
 * See the JSDoc types above for all available options.
 *
 * @type {Object.<string, EventFilterRule>}
 */
export const EVENT_FILTER_CONFIG = {
  $identify: {
    enabled: true,
    sample: 0,
    paths: [{ match: '/cupons/', sample: 0.5 }],
  },
  $feature_flag_called: {
    enabled: false,
  },
  $rageclick: {
    enabled: false,
  },
  $$heatmap: {
    enabled: false,
  },
  $web_vitals: {
    enabled: true,
    sample: 0,
    paths: [{ match: '/cupons/', sample: 0.5 }],
  },
  $pageleave: {
    enabled: false,
  },
  $pageview: {
    enabled: true,
    sample: 0.1,
    paths: [
      { match: '/cupons/', sample: 1.0 },
      { match: '/promocoes/', sample: 0.5 },
    ],
  },
  $set: {
    enabled: true,
    sample: 0.1,
  },
  $autocapture: {
    enabled: true,
    sample: 0,
    paths: [{ match: '/cupons/', sample: 1.0 }],
  },
  '*': {
    enabled: true,
    sample: 0.1,
    paths: [
      { match: '/cupons/', sample: 0.5 },
      { match: '/promocoes/', sample: 0.5 },
    ],
  },
};

/**
 * Extracts pathname from a URL string.
 * Returns '/' on any parse error.
 *
 * @param {string} url
 * @returns {string}
 */
const getPathname = (url) => {
  try {
    return new URL(url).pathname;
  } catch {
    return '/';
  }
};

/**
 * Returns true if pathname contains pattern as a substring.
 *
 * @param {string} pathname
 * @param {string} pattern
 * @returns {boolean}
 */
const matchesPattern = (pathname, pattern) => pathname.includes(pattern);

/**
 * Returns true if the event should be kept given the sample rate.
 *
 * @param {number} rate - Value between 0 and 1 (inclusive)
 * @returns {boolean}
 */
const shouldSample = (rate) => {
  if (rate >= 1) return true;
  if (rate <= 0) return false;
  return Math.random() < rate;
};

/**
 * Factory that returns a `before_send` callback wired to the given config.
 *
 * @param {Object.<string, EventFilterRule>} config
 * @returns {(captureResult: object) => object|null}
 */
export const createBeforeSend = (config) => (captureResult) => {
  const { event, properties } = captureResult;

  // Resolve the rule: exact match first, then '*' only for non-$ events
  const isInternal = event.startsWith('$');
  let rule = config[event];

  if (rule === undefined) {
    if (!isInternal) {
      rule = config['*'];
    }
    // No rule found → send at 100%
    if (rule === undefined) return captureResult;
  }

  // Step 2: enabled check
  if (!rule.enabled) return null;

  const url = properties?.$current_url ?? '';
  const pathname = getPathname(url);

  // Step 3: exclude list
  if (rule.exclude) {
    for (const pattern of rule.exclude) {
      if (matchesPattern(pathname, pattern)) return null;
    }
  }

  // Step 4: weekday restriction
  if (rule.weekdays) {
    const today = new Date().getDay();
    if (!rule.weekdays.includes(today)) return null;
  }

  // Step 5: path-specific sample rate (first match wins)
  if (rule.paths) {
    for (const pathRule of rule.paths) {
      if (matchesPattern(pathname, pathRule.match)) {
        return shouldSample(pathRule.sample) ? captureResult : null;
      }
    }
  }

  // Step 6: fallback sample rate
  if (rule.sample !== undefined) {
    return shouldSample(rule.sample) ? captureResult : null;
  }

  // Step 7: no sample defined → send 100%
  return captureResult;
};
